import DtoolsPdfQuotationPageHeader, {
  type DtoolsPdfQuotationHeaderInfo,
} from "@/components/sales/dtools/DtoolsPdfQuotationPageHeader";
import { BOM_PRINT_FONT } from "@/lib/bomPrintDesign";
import { PROPOSAL_ACCENT, PROPOSAL_TEXT, proposalMoney } from "@/lib/proposalTheme";
import type { DtoolsPortalPaymentTerm } from "@shared/dtoolsDisplaySettings";
import { withPaymentTermsDisplayFallback } from "@shared/proposalCustomerPaymentTerms";
import { resolveQuotationPdfValidityStatement } from "@shared/quotationPdfModel";

type Props = {
  header: DtoolsPdfQuotationHeaderInfo;
  paymentTerms: DtoolsPortalPaymentTerm[];
  createdAt?: string | Date | null;
  linkBExpiresAt?: string | Date | null;
  approvedAt?: string | Date | null;
  status?: string | null;
  pipelineStage?: string | null;
  /** Optional proposal-specific disclaimer paragraphs. */
  disclaimerParagraphs?: string[];
  chromeOnly?: boolean;
  bodyOnly?: boolean;
};

/** Proposal Terms & Conditions for the downloadable quotation PDF — same authority as ProposalRetailTermsCollapsible. */
export default function DtoolsPdfTermsSection({
  header,
  paymentTerms,
  linkBExpiresAt,
  approvedAt,
  status,
  pipelineStage,
  disclaimerParagraphs = [],
  chromeOnly = false,
  bodyOnly = false,
}: Props) {
  const displayTerms = withPaymentTermsDisplayFallback(paymentTerms);
  const validityText = resolveQuotationPdfValidityStatement({
    header: { approvedAt, status, pipelineStage, linkBExpiresAt },
    linkBExpiresAt,
  });
  const disclaimers = disclaimerParagraphs.map((p) => p.trim()).filter(Boolean);

  if (chromeOnly) {
    return <DtoolsPdfQuotationPageHeader info={header} sectionTitle="Terms and Conditions" />;
  }

  const bodyStyle = {
    fontSize: "11px",
    lineHeight: 1.5,
    letterSpacing: 0,
    fontWeight: 400 as const,
    color: "#1A2744",
  };

  return (
    <div className="space-y-3" style={{ fontFamily: BOM_PRINT_FONT, color: PROPOSAL_TEXT }}>
      {!bodyOnly ? <DtoolsPdfQuotationPageHeader info={header} sectionTitle="Terms and Conditions" /> : null}

      <section className="space-y-1.5 pdf-keep-together" data-testid="dtools-pdf-terms-payment">
        <h3
          className="font-semibold uppercase"
          style={{ color: PROPOSAL_ACCENT, fontSize: "9px", letterSpacing: "0.05em" }}
        >
          Payment Terms
        </h3>
        {displayTerms.length > 0 ? (
          <ol className="list-decimal space-y-1 pl-4" style={bodyStyle}>
            {displayTerms.map((term, idx) => (
              <li key={`${term.stage}-${idx}`}>
                {term.fixedAmount != null && term.fixedAmount > 0
                  ? `${proposalMoney.format(term.fixedAmount)} ${term.stage}`
                  : `${Math.round(term.percentage)}% ${term.stage}`}
              </li>
            ))}
          </ol>
        ) : (
          <p className="text-[#6B7280]" style={{ fontSize: "10px" }}>
            Payment terms unavailable.
          </p>
        )}
      </section>

      <section className="space-y-1.5" data-testid="dtools-pdf-terms-conditions">
        <h3
          className="font-semibold uppercase pdf-keep-together"
          style={{ color: PROPOSAL_ACCENT, fontSize: "9px", letterSpacing: "0.05em" }}
        >
          Terms &amp; Conditions
        </h3>
        <ol className="list-decimal space-y-1.5 pl-4" style={bodyStyle}>
          <li className="pdf-keep-together">All prices are stated in SGD.</li>
          <li className="pdf-keep-together">
            Clarity&apos;s Terms &amp; Conditions of Sale shall apply unless otherwise agreed in writing. Full terms are
            available at /terms/retail.
          </li>
          <li className="pdf-keep-together">
            <strong>Minor Installation Accessories</strong> — Clarity may supply and charge for minor accessories
            reasonably required to complete the installation, up to the lower of 0.5% of the Proposal&apos;s Final Price
            or S$500, without further approval.{" "}
            https://portal.claritygroup.com.sg/terms/retail#minor-installation-accessories
          </li>
          <li className="pdf-keep-together">Labour required for cable laying is not included unless otherwise stated.</li>
          <li className="pdf-keep-together">
            Systems are custom designed and specified based on the Customer&apos;s requirements, space, expectations, and
            budget. Alternatives are available upon request.
          </li>
        </ol>
        {validityText ? (
          <p
            className="mt-2 border border-[#1A2744]/10 bg-[#FAFAF7] px-3 py-2 text-[#1A2744] pdf-keep-together"
            data-testid="dtools-pdf-terms-validity"
            style={{ fontSize: "9px", lineHeight: 1.4 }}
          >
            {validityText}
          </p>
        ) : null}
      </section>

      {disclaimers.length > 0 ? (
        <section className="space-y-1.5" data-testid="dtools-pdf-terms-disclaimers">
          <h3
            className="font-semibold uppercase tracking-[0.1em]"
            style={{ color: PROPOSAL_ACCENT, fontSize: "9px", letterSpacing: "0.1em" }}
          >
            Additional Notes
          </h3>
          {disclaimers.map((text, index) => (
            <p key={index} className="whitespace-pre-wrap pdf-keep-together" style={bodyStyle}>
              {text}
            </p>
          ))}
        </section>
      ) : null}
    </div>
  );
}
